"use client";

import React from "react";
import { Loader, CircleCheck, CircleAlert } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
export default function WordHost() {
  return <section id="word-host" className="dark bg-background text-foreground relative min-h-screen overflow-hidden py-24 px-4 sm:px-6 lg:px-8 flex items-center justify-center">
      {}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-[20%] -left-[10%] w-[50%] h-[50%] rounded-full bg-primary/20 blur-[120px] mix-blend-screen" />
        <div className="absolute top-[40%] -right-[10%] w-[40%] h-[60%] rounded-full bg-primary/10 blur-[100px] mix-blend-screen" />
        <div className="absolute -bottom-[20%] left-[20%] w-[60%] h-[50%] rounded-full bg-primary/15 blur-[150px] mix-blend-screen" />
      </div>

      <div className="relative z-10 w-full max-w-3xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-4 text-foreground">
            Ready to host?
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">Join the ZapGO!&nbsp;network. Provide a valuable service to your customers and earn extra income with zero hassle.</p>
        </div>

        <div className="bg-card/40 backdrop-blur-xl border border-border/50 rounded-3xl p-6 md:p-10 shadow-2xl">
          <div className="mb-8 border-b border-border/50 pb-6">
            <h3 className="text-2xl font-semibold text-foreground">Partner Registration Form</h3>
            <p className="text-sm text-muted-foreground mt-1">Fill out the details below and our team will contact you shortly.</p>
          </div>

          <FormProvider formId="partner_form_partners" sectionName="word-host" defaultValues={{
          business_name: "",
          contact_name: "",
          email: "",
          phone: "",
          venue_type: "",
          address: "",
          expected_stations: "",
          message: ""
        }} validationRules={{
          business_name: {
            required: true
          },
          contact_name: {
            required: true
          },
          email: {
            required: true,
            pattern: "email"
          },
          phone: {
            required: true,
            pattern: "phone"
          },
          venue_type: {
            required: true
          },
          address: {
            required: true
          },
          expected_stations: {
            min: 1
          }
        }} submitText="Submit Application" successMessage="Thank you for your interest! We have received your application and will be in touch soon.">
            <PartnerForm />
          </FormProvider>
        </div>
      </div>
    </section>;
}
function PartnerForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center animate-in fade-in zoom-in duration-500">
        <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mb-6">
          <CircleCheck className="w-8 h-8 text-primary" />
        </div>
        <h4 className="text-2xl font-semibold mb-2 text-foreground">Application Received</h4>
        <p className="text-muted-foreground mb-8 max-w-md">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="rounded-full px-8">
          Submit Another Venue
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="business_name" rules={{
        required: "Business name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Business / Venue Name</FormLabel>
              <FormControl>
                <Input placeholder="Restaurant ZapGo!" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="contact_name" rules={{
        required: "Contact person is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Contact Person</FormLabel>
              <FormControl>
                <Input placeholder="" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="jason@voorbeeld.nl" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone number is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+31 6 1234 5678" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="venue_type" rules={{
        required: "Please select a venue type"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Locatie type</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Selecteer uw type locatie" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="Café / Restaurant">Café / Restaurant</SelectItem>
                  <SelectItem value="Bar / Nightclub">Bar / Club</SelectItem>
                  <SelectItem value="Hotel">Hotel</SelectItem>
                  <SelectItem value="Retail Store">Detailhandel</SelectItem>
                  <SelectItem value="Event Space">Evenement locatie</SelectItem>
                  <SelectItem value="Other">Anders</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="expected_stations" rules={{
        min: {
          value: 1,
          message: "Must be at least 1"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Aantal Powerbank Stations</FormLabel>
              <FormControl>
                <Input type="number" min="1" placeholder="e.g., 3" {...field} />
              </FormControl>
              <FormDescription>Geen zorgen, we kunnen dit altijd nog aanpassen!</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="address" rules={{
      required: "Venue address is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Locatie adres</FormLabel>
            <FormControl>
              <Input placeholder="Straat, postcode, stad" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel>Overige informatie / vragen ?</FormLabel>
            <FormControl>
              <textarea className="flex min-h-[120px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-input/30 resize-y" placeholder="Tell us more about your venue or any specific requirements..." {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-start gap-3">
          <CircleAlert className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
          <p className="text-sm text-destructive">{submitError}</p>
        </div>}

      <div className="pt-4">
        <Button type="submit" size="lg" disabled={isSubmitting} className="w-full sm:w-auto min-w-[200px] rounded-full text-base h-12">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 w-5 h-5" />
              Submitting...
            </> : "Submit Application"}
        </Button>
      </div>
    </Form>;
}